import { isSafari, isWeb } from "@memorang/helpers/platform";
import {
	RecordingPresets,
	requestRecordingPermissionsAsync,
	setAudioModeAsync,
	useAudioRecorder,
	useAudioRecorderState,
} from "expo-audio";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../Toast";
import { useWebMetering } from "./useWebMetering";

const MAX_RETRY = 3;

const normalizeMetering = (metering: number) => {
	const minDb = -60;
	const maxDb = -10;
	const clampedDb = Math.min(maxDb, Math.max(minDb, metering));
	return ((clampedDb - minDb) / (maxDb - minDb)) ** 2;
};

type Props = {
	onRecordingComplete?: (uri: string) => void;
	captureError: (context: string, error: Error) => void;
};

const useRecorder = ({ onRecordingComplete, captureError }: Props) => {
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [meterHistory, setMeterHistory] = useState<number[]>([]);
	const [isError, setIsError] = useState(false);
	const [isRecording, setIsRecording] = useState(false);
	const { showToast } = useToast();
	const prevMeteringRef = useRef<number | undefined>(undefined);

	const recordingOptions = useMemo(
		() => ({
			...RecordingPresets.HIGH_QUALITY,
			isMeteringEnabled: true,
			web: {
				...RecordingPresets.HIGH_QUALITY.web,
				mimeType: isSafari ? "audio/mp4" : "audio/webm",
			},
		}),
		[],
	);

	const recorder = useAudioRecorder(recordingOptions);
	const recorderState = useAudioRecorderState(recorder, 100);

	const {
		meterHistory: webMeterHistory,
		startMetering: startWebMetering,
		stopMetering: stopWebMetering,
		clearMeterHistory: clearWebMeterHistory,
	} = useWebMetering(500);

	useEffect(() => {
		if (recorderState.isRecording) {
			setCurrentTime(recorderState.durationMillis || 0);
			setDuration(recorderState.durationMillis || 0);

			if (
				recorderState.metering != null &&
				recorderState.metering !== prevMeteringRef.current
			) {
				prevMeteringRef.current = recorderState.metering;
				const normalizedLevel = normalizeMetering(recorderState.metering);
				setMeterHistory((prev) => [...prev, normalizedLevel]);
			}
		}
	}, [
		recorderState.isRecording,
		recorderState.durationMillis,
		recorderState.metering,
	]);

	const startRecording = useCallback(
		async (retryCount: number) => {
			try {
				const permissionResponse = await requestRecordingPermissionsAsync();
				if (permissionResponse?.status !== "granted") {
					throw new Error("Permission not granted.");
				}
				await setAudioModeAsync({
					allowsRecording: true,
					playsInSilentMode: true,
					allowsBackgroundRecording: true,
				});
				await recorder.prepareToRecordAsync(recordingOptions);
				recorder.record();
				await startWebMetering();
				setIsError(false);
				setIsRecording(true);
				setMeterHistory([]);
			} catch (error) {
				console.error("Failed to start recording", error);
				if (retryCount < MAX_RETRY) {
					await new Promise((resolve) => setTimeout(resolve, 200));
					await startRecording(retryCount + 1);
					return;
				}
				const recordingError = new Error("Failed to start recording");
				captureError("audio_recording_start", recordingError);
				setIsError(true);
				showToast({
					message:
						error instanceof Error
							? error.message
							: "Failed to start recording.",
				});
			}
		},
		[showToast, startWebMetering, captureError, recorder, recordingOptions],
	);

	const stopRecording = useCallback(async () => {
		stopWebMetering();
		await recorder.stop();
		const uri = recorder.uri;
		setIsRecording(false);
		setCurrentTime(0);
		await setAudioModeAsync({
			allowsRecording: false,
			playsInSilentMode: true,
		});
		if (uri) {
			onRecordingComplete?.(uri);
		}
	}, [onRecordingComplete, stopWebMetering, recorder]);

	const clearMeterHistory = useCallback(() => {
		setMeterHistory([]);
		clearWebMeterHistory();
	}, [clearWebMeterHistory]);

	const unloadRecording = useCallback(async () => {
		if (recorder.isRecording) {
			await recorder.stop();
			await stopWebMetering();
		}
	}, [stopWebMetering, recorder]);

	useEffect(() => {
		return () => {
			unloadRecording();
		};
	}, [unloadRecording]);

	const value = useMemo(() => {
		return {
			isRecording,
			currentTime,
			duration,
			meterHistory: isWeb ? webMeterHistory : meterHistory,
			isError,
			startRecording,
			stopRecording,
			clearMeterHistory,
			unloadRecording,
		};
	}, [
		isRecording,
		currentTime,
		duration,
		meterHistory,
		webMeterHistory,
		isError,
		startRecording,
		stopRecording,
		clearMeterHistory,
		unloadRecording,
	]);

	return value;
};

export default useRecorder;
